// UX telemetry (TASK-071): reconstructable user sessions.
// Captures referrer/UTM + screen geometry once per session, then a compact
// event stream: mouse position sampled at 2Hz (page coordinates), clicks
// (coords + selector + trimmed text), scroll, route changes, visibility,
// input focus (field NAME only — values are never read). Batched every 5s
// to POST /api/ux/ingest; pagehide flushes via sendBeacon. Fail-silent:
// telemetry must never break the product, so the tracker disables itself
// after repeated send failures.

import { attributionPath, attributionReferrer, readFirstTouchAttribution, sanitizeAcquisitionAttribution } from '../lib/acquisitionAttribution.js';

const FLUSH_INTERVAL_MS = 5000;
const MOUSE_SAMPLE_MS = 500; // 2Hz per owner spec
const MAX_BUFFER = 400;
const MAX_SEND_FAILURES = 3;

let state = null;

function readUtm(search) {
  const p = new URLSearchParams(search);
  return {
    utmSource: p.get('utm_source') || null,
    utmMedium: p.get('utm_medium') || null,
    utmCampaign: p.get('utm_campaign') || null,
    utmTerm: p.get('utm_term') || null,
    utmContent: p.get('utm_content') || null,
  };
}

// Short, replay-useful selector: tag#id.cls1.cls2 (max 2 classes), plus the
// nearest data-testid / aria-label when present.
function describeTarget(el) {
  try {
    if (!el || !el.tagName) return null;
    let s = el.tagName.toLowerCase();
    if (el.id) s += `#${el.id}`;
    if (typeof el.className === 'string' && el.className) {
      s += el.className.trim().split(/\s+/).slice(0, 2).map(c => `.${c}`).join('');
    }
    const label = el.getAttribute?.('aria-label') || el.dataset?.testid;
    if (label) s += `[${String(label).slice(0, 40)}]`;
    const text = (el.innerText || '').trim().slice(0, 40);
    return text ? `${s} "${text}"` : s;
  } catch {
    return null;
  }
}

function push(event) {
  if (!state || state.dead) return;
  state.buffer.push(event);
  if (state.buffer.length >= MAX_BUFFER) flush();
}

async function flush(useBeacon = false) {
  if (!state || state.dead || state.buffer.length === 0) return;
  const events = state.buffer;
  state.buffer = [];
  const payload = JSON.stringify({
    session: state.session,
    seq: state.seq++,
    events,
  });
  const url = `${state.apiBase}/api/ux/ingest`;
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    });
    state.failures = res.ok ? 0 : state.failures + 1;
  } catch {
    state.failures += 1;
  }
  if (state.failures >= MAX_SEND_FAILURES) state.dead = true;
}

export function identifyUxUser(userId) {
  if (state) state.session.userId = userId || null;
}

// TASK-070: the attribution snapshot the signup call sends to auth-worker so
// users carry "where did they come from" from day one. Falls back to reading
// the URL directly when the tracker is disabled (kill-switch/storage-blocked).
export function getUxAttribution() {
  try {
    const firstTouch = readFirstTouchAttribution();
    if (firstTouch) return sanitizeAcquisitionAttribution(firstTouch);
    if (state) {
      const s = state.session;
      return sanitizeAcquisitionAttribution({
        referrer: s.referrer,
        utmSource: s.utmSource,
        utmMedium: s.utmMedium,
        utmCampaign: s.utmCampaign,
        landingPath: s.entryPath,
        uxSessionId: s.id,
      });
    }
    return sanitizeAcquisitionAttribution({
      referrer: document.referrer || null,
      ...(({ utmSource, utmMedium, utmCampaign }) => ({ utmSource, utmMedium, utmCampaign }))(readUtm(location.search)),
      landingPath: location.pathname + location.search,
      uxSessionId: null,
    });
  } catch {
    return { referrer: null, utmSource: null, utmMedium: null, utmCampaign: null, landingPath: null, uxSessionId: null };
  }
}

export function trackUxEvent(name, data) {
  push({ t: 'ev', ts: Date.now(), name: String(name).slice(0, 64), ...(data ? { d: data } : {}) });
}

export function startUxTracker({ apiBase, app = 'app' }) {
  if (state || typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem('dathent.uxlog.disabled') === '1') return;
  } catch { /* storage unavailable — proceed */ }

  state = {
    apiBase,
    seq: 0,
    buffer: [],
    failures: 0,
    dead: false,
    lastMouse: null,
    lastSampled: null,
    session: {
      id: (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      app,
      userId: null,
      entryPath: attributionPath(location.pathname),
      referrer: attributionReferrer(document.referrer),
      ...readUtm(location.search),
      screenW: window.screen?.width ?? null,
      screenH: window.screen?.height ?? null,
      viewportW: window.innerWidth,
      viewportH: window.innerHeight,
      dpr: window.devicePixelRatio || 1,
      language: navigator.language || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    },
  };

  push({ t: 'pv', ts: Date.now(), path: state.session.entryPath });

  // Mouse: remember the latest position; the 2Hz sampler records it only
  // when it moved since the last sample (an idle cursor emits nothing).
  window.addEventListener('mousemove', (e) => {
    if (state) state.lastMouse = { x: e.pageX, y: e.pageY };
  }, { passive: true });
  state.mouseTimer = window.setInterval(() => {
    const m = state?.lastMouse;
    if (!m) return;
    const prev = state.lastSampled;
    if (prev && prev.x === m.x && prev.y === m.y) return;
    state.lastSampled = { ...m };
    push({ t: 'mv', ts: Date.now(), x: m.x, y: m.y });
  }, MOUSE_SAMPLE_MS);

  window.addEventListener('click', (e) => {
    push({ t: 'cl', ts: Date.now(), x: e.pageX, y: e.pageY, el: describeTarget(e.target) });
  }, { capture: true, passive: true });

  let lastScrollTs = 0;
  window.addEventListener('scroll', () => {
    const now = Date.now();
    if (now - lastScrollTs < 1000) return;
    lastScrollTs = now;
    push({ t: 'sc', ts: now, y: window.scrollY });
  }, { passive: true });

  window.addEventListener('focusin', (e) => {
    const el = e.target;
    if (el && /^(input|textarea|select)$/i.test(el.tagName || '')) {
      push({ t: 'foc', ts: Date.now(), field: el.name || el.id || el.type || 'unnamed' });
    }
  }, { passive: true });

  document.addEventListener('visibilitychange', () => {
    push({ t: 'vis', ts: Date.now(), s: document.visibilityState });
    if (document.visibilityState === 'hidden') flush(true);
  });

  // SPA route changes: History API hook + popstate.
  const emitNav = () => push({ t: 'pv', ts: Date.now(), path: attributionPath(location.pathname) });
  for (const method of ['pushState', 'replaceState']) {
    const original = history[method];
    history[method] = function (...args) {
      const result = original.apply(this, args);
      emitNav();
      return result;
    };
  }
  window.addEventListener('popstate', emitNav);

  state.flushTimer = window.setInterval(() => flush(), FLUSH_INTERVAL_MS);
  window.addEventListener('pagehide', () => flush(true));
}
